import type { Listener } from './usePageData'
import { usePageData } from './usePageData'
import { shouldRunListenerOnClient } from '@/common/utils/webClientExec'
import { FormProviderKey, ElementContextKey } from '@/components/platform/injection-keys'
import { inject } from 'vue'
import { useRoute } from 'vue-router'
import { useAppStore } from '@/stores/app'
import { filterEventListeners, resolvePageEventListeners } from '@/common/utils/eventListeners'
import { getListenerEventNamesToMatch } from '@/components/platform/events/event-map'

function levelEquals(a: string | undefined, b: string | undefined): boolean {
  return String(a ?? '').toLowerCase() === String(b ?? '').toLowerCase()
}

/** Id корня формы: слот диалога, jsResult.form_id, атрибуты разметки — для сопоставления с listener.id */
function collectFormRootIdCandidates(pd: any, requestedId: number | string): number[] {
  const out = new Set<number>()
  const add = (v: unknown) => {
    if (v == null || v === '') return
    const n = typeof v === 'number' ? v : parseInt(String(v), 10)
    if (Number.isFinite(n) && !Number.isNaN(n)) out.add(n)
  }
  add(requestedId)
  if (!pd || typeof pd !== 'object') return [...out]
  add(pd.jsResult?.form_id)
  add(pd.form?.form?._attributes?.id)
  add(pd.form?.form?._attributes?.fid)
  add(pd.form?._attributes?.id)
  add(pd.formId)
  add(pd.$fid)
  add(pd.status)
  return [...out]
}

/**
 * Компоновка для работы с event listeners объектов формы
 */
export function useListeners() {
  const { pageListeners, pageData } = usePageData()
  const route = useRoute()
  const formProvider = inject(FormProviderKey, null)
  const elementContext = inject(ElementContextKey, null)
  const appStore = useAppStore()

  const resolveListenerPageSlot = (): string =>
    String((elementContext as { pageDataSlot?: string } | null)?.pageDataSlot ?? '').trim()

  /** Listeners для текущего контекста: слот страницы, registry form id или pageData. */
  const listenersForContext = (): Listener[] =>
    resolvePageEventListeners(appStore.pagesData as Record<string, unknown>, {
      pageDataSlot: resolveListenerPageSlot(),
      registryFormId: elementContext?.formId,
      injectedListeners: pageListeners.value || [],
    })

  /**
   * Получить listeners для конкретного объекта формы
   * @param level - тип объекта ('element', 'panel', 'form')
   * @param id - идентификатор объекта
   * @param event_name - имя события (опционально, для фильтрации)
   */
  const getListeners = (
    level: string,
    id: number | string,
    event_name?: string
  ): Listener[] => {
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id
    if (Number.isNaN(numericId) && level === 'element') return []

    const listeners = listenersForContext()
    const matched = filterEventListeners(listeners, level, id, event_name)
    if (!levelEquals(level, 'form')) {
      return matched
    }

    const eventNamesToMatch = event_name ? getListenerEventNamesToMatch(event_name) : null
    return listeners.filter((listener: Listener) => {
      if (listener.to_remove) return false
      const typeMatch = levelEquals(listener.level, level)
      const listenerIdNum = listener.id != null ? Number(listener.id) : NaN
      const candidates = collectFormRootIdCandidates(pageData.value as any, numericId)
      const idMatch = candidates.some(
        (c) => c === listenerIdNum || String(listener.id) === String(c),
      )
      const ev = String(listener.event_name ?? '')
      const eventMatch = eventNamesToMatch
        ? eventNamesToMatch.some((n) => String(n).toLowerCase() === ev.toLowerCase())
        : true
      return typeMatch && idMatch && eventMatch
    })
  }

  /**
   * Получить контекст формы (formId, panelId) на основе уровня и id listener'а
   */
  const getFormContext = (level: string, id: number | string): { formId?: string; panelId?: string } => {
    if (elementContext?.formId) {
      return {
        formId: elementContext.formId,
        panelId: elementContext.panelId
      }
    }

    // level - это форма, используем id как formId
    if (level === 'form') {
      const numericFormRootId = typeof id === 'string' ? parseInt(id, 10) : id
      if (numericFormRootId === -1 || id === '-1') {
        const rid = route?.params?.id
        if (typeof rid === 'string' && rid && appStore.pagesData[rid]) {
          return { formId: rid }
        }
        const cp = appStore.currentPageId
        if (cp && cp !== 'home' && appStore.pagesData[cp]) {
          return { formId: String(cp) }
        }
        const pd = pageData.value as any
        const resolved =
          pd?.formId ??
          pd?.jsResult?.form_id ??
          pd?.form?.form?._attributes?.id ??
          pd?.form?._attributes?.id
        if (resolved != null && resolved !== '') {
          return { formId: String(resolved) }
        }
      }
      return { formId: String(id) }
    }

    // level - это панель, пытаемся найти formId из pageData
    if (level === 'panel') {
      const formIds = Object.keys(appStore.pagesData)
      if (formIds.length > 0) {
        const formId = formIds[0]
        return { formId, panelId: String(id) }
      }
    }

    // level - это элемент, пытаемся найти formId и panelId из pageData
    if (level === 'element') {
      const formIds = Object.keys(appStore.pagesData)
      if (formIds.length > 0) {
        const formId = formIds[0]
        const formData = appStore.pagesData[formId]
        if (formData?.form) {
          const panelIds = Object.keys(formData.form)
          if (panelIds.length > 0) {
            return { formId, panelId: panelIds[0] }
          }
        }
        return { formId }
      }
    }

    return {}
  }

  /**
   * Выполнить код из строки, которая может содержать определение функции
   */
  const executeListenerFunction = async (
    functionCode: string,
    formContext: { formId?: string; panelId?: string },
    context?: any
  ): Promise<void> => {
    if (!formProvider) {
      console.warn('FormProvider not available for executing listener function')
      return
    }

    // TODO: попробовать иные обработки
    // Извлекаем код из строки функции (либо обернуть и передать контекст?)
    // Может быть: "function() { fillText(); }" или просто "fillText();"
    let codeToExecute = functionCode.trim()

    // Если строка начинается с "function", извлекаем тело функции
    if (codeToExecute.startsWith('function')) {
      // Удаляем "function" и параметры, оставляем только тело
      const functionMatch = codeToExecute.match(/function\s*\([^)]*\)\s*\{([\s\S]*)\}/)
      if (functionMatch && functionMatch[1]) {
        codeToExecute = functionMatch[1].trim()
      } else {
        // Попытка извлечь тело другим способом (для многострочных функций)
        const braceMatch = codeToExecute.match(/\{([\s\S]*)\}/)
        if (braceMatch && braceMatch[1]) {
          codeToExecute = braceMatch[1].trim()
        }
      }
    }

    const pageSlotId = resolveListenerPageSlot()

    try {
      await formProvider.runEvent(codeToExecute, {
        ...formContext,
        pageSlotId: pageSlotId || undefined,
        context,
      })
    } catch (err) {
      console.error('executeListenerFunction runEvent error', err)
      throw err
    }
  }

  /**
   * Вызвать подписанные listeners для конкретного объекта и события
   * @param level - тип объекта (уровень)
   * @param id - идентификатор объекта
   * @param event_name - имя события
   * @param context - контекст для передачи в функцию listener (опционально)
   */
  const invokeListeners = async (
    level: string,
    id: number | string,
    event_name: string,
    context?: any
  ): Promise<void> => {
    const listeners = getListeners(level, id, event_name)
    if (listeners.length === 0) return

    if (!formProvider) {
      console.warn('FormProvider not available for invoking listeners')
      return
    }

    // Контекст формы: при вызове из элемента используем formId/panelId из elementContext, иначе — getFormContext
    const formContext = (elementContext?.formId != null)
      ? { formId: String(elementContext.formId), panelId: elementContext.panelId != null ? String(elementContext.panelId) : undefined }
      : getFormContext(level, id)

    // Вызываем каждый listener
    for (const listener of listeners) {
      try {
        if (!shouldRunListenerOnClient(listener)) continue
        // Выполняем функцию listener через runCode
        // listener.function = "fillText();"
        // listener.function = "en.getData().setField('test01', 'тест листенера'); en.refillElement('test01');"
        // console.log(context)
        // formProvider.runCode(listener.function, formContext || {})
        await executeListenerFunction(listener.function, formContext, context || {})
      } catch (error) {
        console.error(
          `Error invoking listener for ${level} ${id}, event ${event_name}:`,
          error
        )
      }
    }
  }

  /**
   * Проверить, есть ли listeners для объекта и события
   */
  const hasListeners = (
    level: string,
    id: number | string,
    event_name?: string
  ): boolean => {
    return getListeners(level, id, event_name).length > 0
  }

  return {
    getListeners,
    invokeListeners,
    hasListeners,
    pageListeners
  }
}


